import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon, LockClosedIcon } from '@heroicons/react/24/outline';

import { CheckboxField, Checkbox } from '@oysterjs/ui/checkbox';
import { Combobox } from '@oysterjs/ui/combobox';
import { Description, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';

import type { HtmlDocumentTemplateStyleInput } from '~/api/graphql';
import { computeTint, contrastTextColor } from '~/components/library/lib/color';
import { FONT_FAMILY_COMBOBOX_OPTIONS } from '~/components/library/lib/font-options';

import { ColorInput } from './color-input';

/**
 * Style overrides for an HTML document template — colours, typography,
 * heading rules, table styling, and page margins. Composed as five
 * collapsible sections so the slideout stays scannable; each section
 * mutates the same parent `HtmlDocumentTemplateStyleInput` state.
 *
 * The component intentionally has no knowledge of submission, navigation,
 * or visibility — it's a controlled style editor.
 */
export function HtmlEditorStylePanel(props: {
  value: HtmlDocumentTemplateStyleInput | null;
  onChange: React.Dispatch<React.SetStateAction<HtmlDocumentTemplateStyleInput | null>>;
  /** True when any header/footer slot has an image, locking top/bottom margins. */
  hasHeaderFooterImageSlots: boolean;
}) {
  const { value, onChange } = props;

  const [colorsExpanded, setColorsExpanded] = React.useState(false);
  const [typographyExpanded, setTypographyExpanded] = React.useState(false);
  const [headingsExpanded, setHeadingsExpanded] = React.useState(false);
  const [tableExpanded, setTableExpanded] = React.useState(false);
  const [pageExpanded, setPageExpanded] = React.useState(false);

  return (
    <>
      <ColorsSection
        value={value}
        onChange={onChange}
        expanded={colorsExpanded}
        onToggle={() => setColorsExpanded((v) => !v)}
      />
      <TypographySection
        value={value}
        onChange={onChange}
        expanded={typographyExpanded}
        onToggle={() => setTypographyExpanded((v) => !v)}
      />
      <HeadingsSection
        value={value}
        onChange={onChange}
        expanded={headingsExpanded}
        onToggle={() => setHeadingsExpanded((v) => !v)}
      />
      <TableSection
        value={value}
        onChange={onChange}
        expanded={tableExpanded}
        onToggle={() => setTableExpanded((v) => !v)}
      />
      <PageSection
        value={value}
        onChange={onChange}
        hasHeaderFooterImageSlots={props.hasHeaderFooterImageSlots}
        expanded={pageExpanded}
        onToggle={() => setPageExpanded((v) => !v)}
      />
    </>
  );
}

type SectionProps = {
  value: HtmlDocumentTemplateStyleInput | null;
  onChange: React.Dispatch<React.SetStateAction<HtmlDocumentTemplateStyleInput | null>>;
  expanded: boolean;
  onToggle: () => void;
};

function SectionHeader(props: { label: string; expanded: boolean; onToggle: () => void }) {
  return (
    <Headless.Button
      type="button"
      data-slot="label"
      onClick={props.onToggle}
      className="flex w-full items-center justify-between text-left"
    >
      <Label className="font-medium">{props.label}</Label>
      {props.expanded ? (
        <ChevronUpIcon className="ml-4 h-5 w-5 flex-shrink-0 text-neutral-500 dark:text-neutral-400" />
      ) : (
        <ChevronDownIcon className="ml-4 h-5 w-5 flex-shrink-0 text-neutral-500 dark:text-neutral-400" />
      )}
    </Headless.Button>
  );
}

const COLOR_PAIRS = [
  {
    baseField: 'primaryColorHex' as const,
    lightField: 'primaryColorLightHex' as const,
    baseLabel: 'Primary',
    lightLabel: 'Primary Light',
    basePlaceholder: '#2563eb'
  },
  {
    baseField: 'secondaryColorHex' as const,
    lightField: 'secondaryColorLightHex' as const,
    baseLabel: 'Secondary',
    lightLabel: 'Secondary Light',
    basePlaceholder: '#64748b'
  }
];

function ColorsSection({ value, onChange, expanded, onToggle }: SectionProps) {
  // Setting primary or secondary clears linked heading/table colour overrides
  // so the new brand colour can flow through. Light variants are independent.
  const setBaseAndClearLinked = (
    field: 'primaryColorHex' | 'secondaryColorHex',
    next: string | undefined
  ) =>
    onChange((s) => {
      const updated: HtmlDocumentTemplateStyleInput = { ...s, [field]: next };
      if (field === 'primaryColorHex') {
        updated.elements = {
          ...s?.elements,
          headings: s?.elements?.headings
            ? {
                ...s.elements.headings,
                h1:
                  s.elements.headings.h1 != null
                    ? { ...s.elements.headings.h1, colorHex: undefined }
                    : undefined,
                h2:
                  s.elements.headings.h2 != null
                    ? { ...s.elements.headings.h2, colorHex: undefined }
                    : undefined
              }
            : undefined,
          table:
            s?.elements?.table != null
              ? {
                  ...s.elements.table,
                  headerBackgroundColorHex: undefined,
                  headerColorHex: undefined,
                  zebraStripeColorHex: undefined
                }
              : undefined
        };
      } else {
        updated.elements = {
          ...s?.elements,
          headings: s?.elements?.headings
            ? {
                ...s.elements.headings,
                h3:
                  s.elements.headings.h3 != null
                    ? { ...s.elements.headings.h3, colorHex: undefined }
                    : undefined
              }
            : undefined
        };
      }
      return updated;
    });

  return (
    <Field>
      <SectionHeader label="Colors" expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <>
          <Description>
            Override the default color palette. Leave empty to use the base style values.
          </Description>
          <Description className="mt-2 italic">
            Changing primary or secondary here updates brand-linked colors across the document and
            clears heading or table color overrides tied to those colors.
          </Description>
          <div className="mt-2 flex flex-wrap gap-6">
            {COLOR_PAIRS.map(
              ({ baseField, lightField, baseLabel, lightLabel, basePlaceholder }) => {
                const baseValue = value?.[baseField];
                const lightValue = value?.[lightField];
                const autoLight = baseValue ? computeTint(baseValue, 0.12) : '#e8eaed';
                return (
                  <div key={baseField} className="flex items-end gap-4">
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-600 dark:text-neutral-400">
                        {baseLabel}
                      </Label>
                      <ColorInput
                        value={baseValue ?? basePlaceholder ?? '#ffffff'}
                        textValue={baseValue ?? ''}
                        onChange={(hex) => setBaseAndClearLinked(baseField, hex)}
                        placeholder={basePlaceholder}
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-600 dark:text-neutral-400">
                        {lightLabel}
                        {!lightValue && (
                          <span className="ml-1 text-neutral-400 dark:text-neutral-500 italic">
                            (auto)
                          </span>
                        )}
                      </Label>
                      <ColorInput
                        value={lightValue ?? autoLight ?? '#ffffff'}
                        textValue={lightValue ?? ''}
                        onChange={(hex) => onChange((s) => ({ ...s, [lightField]: hex }))}
                        placeholder={autoLight ?? `Auto from ${baseLabel}`}
                      />
                    </div>
                  </div>
                );
              }
            )}
            <div>
              <Label className="mb-1 block text-xs text-neutral-600 dark:text-neutral-400">
                Background
              </Label>
              <ColorInput
                value={value?.page?.backgroundColorHex ?? '#ffffff'}
                textValue={value?.page?.backgroundColorHex ?? ''}
                onChange={(hex) =>
                  onChange((s) => ({
                    ...s,
                    page: { ...s?.page, backgroundColorHex: hex }
                  }))
                }
                placeholder="#ffffff"
              />
            </div>
          </div>
        </>
      )}
    </Field>
  );
}

const FONT_SIZE_OPTIONS_LARGE = [
  '8',
  '9',
  '10',
  '10.5',
  '11',
  '12',
  '13',
  '14',
  '16',
  '18',
  '20',
  '24',
  '28',
  '36',
  '48',
  '72'
];

const FONT_SIZE_OPTIONS_PARAGRAPH = [
  '8',
  '9',
  '10',
  '10.5',
  '11',
  '12',
  '13',
  '14',
  '16',
  '18',
  '20',
  '24'
];

const LINE_HEIGHT_OPTIONS = ['1', '1.15', '1.25', '1.5', '1.75', '2'];
const LINE_HEIGHT_LABELS: Record<string, string> = {
  '1': '1.0 – Single',
  '1.5': '1.5 – Comfortable',
  '2': '2.0 – Double'
};
const SPACING_OPTIONS = ['0', '0.25', '0.5', '0.75', '1', '1.25', '1.5', '2'];

function NumericSelect(props: {
  value: number | null | undefined;
  options: readonly string[];
  emptyLabel?: string;
  onChange: (next: number | undefined) => void;
  className?: string;
  labels?: Record<string, string>;
  parser?: (raw: string) => number;
  unit?: string;
}) {
  const labels = props.labels ?? {};
  const parser = props.parser ?? ((v: string) => parseFloat(v));
  return (
    <Select
      value={props.value?.toString() ?? ''}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          props.onChange(undefined);
          return;
        }
        const n = parser(raw);
        props.onChange(!Number.isNaN(n) ? n : undefined);
      }}
      className={props.className}
    >
      <option value="">{props.emptyLabel ?? '—'}</option>
      {props.options.map((o) => (
        <option key={o} value={o}>
          {labels[o] ?? `${o}${props.unit ? ` ${props.unit}` : ''}`}
        </option>
      ))}
    </Select>
  );
}

function TypographySection({ value, onChange, expanded, onToggle }: SectionProps) {
  const updateTypography = (
    patch: Partial<NonNullable<HtmlDocumentTemplateStyleInput['typography']>>
  ) => onChange((s) => ({ ...s, typography: { ...s?.typography, ...patch } }));
  const updateParagraph = (
    patch: Partial<
      NonNullable<NonNullable<HtmlDocumentTemplateStyleInput['elements']>['paragraph']>
    >
  ) =>
    onChange((s) => ({
      ...s,
      elements: { ...s?.elements, paragraph: { ...s?.elements?.paragraph, ...patch } }
    }));

  return (
    <Field>
      <SectionHeader label="Typography" expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <>
          <Description>
            Customize font family, size, line height, and paragraph spacing.
          </Description>
          <div className="mt-2 space-y-3">
            <div className="space-y-2">
              <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                Font family
              </Label>
              <Combobox
                minQueryLength={0}
                maxOptions={50}
                placeholder="Search preset fonts…"
                value={
                  FONT_FAMILY_COMBOBOX_OPTIONS.some(
                    (o) => o.value === (value?.typography?.fontFamily ?? '')
                  )
                    ? (value?.typography?.fontFamily ?? '')
                    : undefined
                }
                options={[...FONT_FAMILY_COMBOBOX_OPTIONS]}
                onChange={(v) => {
                  const next = typeof v === 'string' ? v : '';
                  if (next) updateTypography({ fontFamily: next });
                }}
              />
            </div>
            <div className="flex items-end gap-3">
              <div>
                <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                  Size (pt)
                </Label>
                <NumericSelect
                  value={value?.typography?.fontSizePt}
                  options={FONT_SIZE_OPTIONS_LARGE}
                  unit="pt"
                  onChange={(n) => updateTypography({ fontSizePt: n })}
                  className="min-w-[5.5rem]"
                />
              </div>
              <div>
                <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                  Line height
                </Label>
                <NumericSelect
                  value={value?.typography?.lineHeight}
                  options={LINE_HEIGHT_OPTIONS}
                  labels={LINE_HEIGHT_LABELS}
                  emptyLabel="Default"
                  onChange={(n) => updateTypography({ lineHeight: n })}
                  className="w-44"
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="font-medium">Paragraph</Label>
              <div className="flex items-end gap-3 mb-3">
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Size (pt)
                  </Label>
                  <NumericSelect
                    value={value?.elements?.paragraph?.fontSizePt}
                    options={FONT_SIZE_OPTIONS_PARAGRAPH}
                    unit="pt"
                    onChange={(n) => updateParagraph({ fontSizePt: n })}
                    className="min-w-[5.5rem]"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Line ht.
                  </Label>
                  <NumericSelect
                    value={value?.elements?.paragraph?.lineHeight}
                    options={LINE_HEIGHT_OPTIONS}
                    labels={LINE_HEIGHT_LABELS}
                    emptyLabel="Default"
                    onChange={(n) => updateParagraph({ lineHeight: n })}
                    className="min-w-[6.25rem]"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Color
                  </Label>
                  <ColorInput
                    value={
                      value?.elements?.paragraph?.colorHex ??
                      value?.typography?.colorHex ??
                      '#334155'
                    }
                    textValue={
                      value?.elements?.paragraph?.colorHex ?? value?.typography?.colorHex ?? ''
                    }
                    onChange={(hex) =>
                      onChange((s) => ({
                        ...s,
                        typography: { ...s?.typography, colorHex: hex },
                        elements: {
                          ...s?.elements,
                          paragraph: { ...s?.elements?.paragraph, colorHex: hex }
                        }
                      }))
                    }
                    placeholder="#334155"
                    inputClassName="w-20"
                  />
                </div>
              </div>
              <div className="flex items-end gap-3 mb-3">
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Space above
                  </Label>
                  <NumericSelect
                    value={value?.elements?.paragraph?.marginTopEm}
                    options={SPACING_OPTIONS}
                    emptyLabel="Default"
                    onChange={(n) => updateParagraph({ marginTopEm: n })}
                    className="w-28"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Space below
                  </Label>
                  <NumericSelect
                    value={value?.elements?.paragraph?.marginBottomEm}
                    options={SPACING_OPTIONS}
                    emptyLabel="Default"
                    onChange={(n) => updateParagraph({ marginBottomEm: n })}
                    className="w-28"
                  />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Lines before page break
                  </Label>
                  <NumericSelect
                    value={value?.elements?.paragraph?.orphans}
                    options={['1', '2', '3', '4', '5']}
                    emptyLabel="–"
                    parser={(s) => parseInt(s, 10)}
                    onChange={(n) => updateParagraph({ orphans: n })}
                    className="w-16"
                  />
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                    Lines kept at bottom
                  </p>
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    Lines after page break
                  </Label>
                  <NumericSelect
                    value={value?.elements?.paragraph?.widows}
                    options={['1', '2', '3', '4', '5']}
                    emptyLabel="–"
                    parser={(s) => parseInt(s, 10)}
                    onChange={(n) => updateParagraph({ widows: n })}
                    className="w-16"
                  />
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                    Lines kept at top
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Field>
  );
}

const HEADING_FONT_SIZES = [
  '8',
  '9',
  '10',
  '10.5',
  '11',
  '12',
  '13',
  '14',
  '16',
  '18',
  '20',
  '22',
  '24',
  '28',
  '36'
];

function HeadingsSection({ value, onChange, expanded, onToggle }: SectionProps) {
  return (
    <Field>
      <SectionHeader label="Headings" expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <>
          <Description>
            Customize heading sizes, colors, weights, and spacing for H1, H2, and H3.
          </Description>
          <div className="mt-2 space-y-6">
            {(['h1', 'h2', 'h3'] as const).map((el) => {
              const data = value?.elements?.headings?.[el];
              const updateHeading = (patch: object) =>
                onChange((s) => ({
                  ...s,
                  elements: {
                    ...s?.elements,
                    headings: {
                      ...s?.elements?.headings,
                      [el]: { ...s?.elements?.headings?.[el], ...patch }
                    }
                  }
                }));
              // H1/H2 inherit primary by default; H3 inherits secondary.
              const headingAutoColor =
                el === 'h3' ? (value?.secondaryColorHex ?? null) : (value?.primaryColorHex ?? null);
              return (
                <div key={el} className="space-y-2">
                  <Label className="font-medium">{el.toUpperCase()}</Label>
                  <div className="flex items-center gap-4 mb-2">
                    <CheckboxField>
                      <Checkbox
                        color="sky"
                        checked={(data?.fontWeight ?? 0) >= 600}
                        onChange={(checked) => updateHeading({ fontWeight: checked ? 600 : 400 })}
                      />
                      <Label className="text-xs">Bold</Label>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox
                        color="sky"
                        checked={data?.fontStyle === 'italic'}
                        onChange={(checked) =>
                          updateHeading({ fontStyle: checked ? 'italic' : 'normal' })
                        }
                      />
                      <Label className="text-xs">Italic</Label>
                    </CheckboxField>
                    <CheckboxField>
                      <Checkbox
                        color="sky"
                        checked={data?.pageBreakBefore ?? false}
                        onChange={(checked) =>
                          updateHeading({ pageBreakBefore: checked ? true : undefined })
                        }
                      />
                      <Label className="text-xs">Page break before</Label>
                    </CheckboxField>
                  </div>
                  <div className="flex items-end gap-3 mb-2">
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                        Size (pt)
                      </Label>
                      <NumericSelect
                        value={data?.fontSizePt}
                        options={HEADING_FONT_SIZES}
                        unit="pt"
                        onChange={(n) => updateHeading({ fontSizePt: n })}
                        className="min-w-[5.5rem]"
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                        Line ht.
                      </Label>
                      <NumericSelect
                        value={data?.lineHeight}
                        options={LINE_HEIGHT_OPTIONS}
                        labels={LINE_HEIGHT_LABELS}
                        emptyLabel="Default"
                        onChange={(n) => updateHeading({ lineHeight: n })}
                        className="min-w-[6.25rem]"
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                        Color
                        {headingAutoColor && !data?.colorHex && (
                          <span className="ml-1 text-neutral-400 dark:text-neutral-500 italic">
                            (auto)
                          </span>
                        )}
                      </Label>
                      <ColorInput
                        value={data?.colorHex ?? headingAutoColor ?? '#0f172a'}
                        textValue={data?.colorHex ?? ''}
                        onChange={(hex) => updateHeading({ colorHex: hex })}
                        placeholder={headingAutoColor ?? '#0f172a'}
                        inputClassName="w-20"
                      />
                    </div>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                        Space above
                      </Label>
                      <NumericSelect
                        value={data?.marginTopEm}
                        options={SPACING_OPTIONS}
                        emptyLabel="Default"
                        onChange={(n) => updateHeading({ marginTopEm: n })}
                        className="w-28"
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                        Space below
                      </Label>
                      <NumericSelect
                        value={data?.marginBottomEm}
                        options={SPACING_OPTIONS}
                        emptyLabel="Default"
                        onChange={(n) => updateHeading({ marginBottomEm: n })}
                        className="w-28"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Field>
  );
}

function TableSection({ value, onChange, expanded, onToggle }: SectionProps) {
  const primaryHex = value?.primaryColorHex ?? null;
  const bodyTextColor = value?.typography?.colorHex ?? '#000000';
  const autoBg = primaryHex ?? null;
  const autoText = primaryHex ? contrastTextColor(primaryHex, bodyTextColor) : null;
  const explicitBg = value?.elements?.table?.headerBackgroundColorHex;
  const explicitText = value?.elements?.table?.headerColorHex;

  const updateTable = (patch: object) =>
    onChange((s) => ({
      ...s,
      elements: { ...s?.elements, table: { ...s?.elements?.table, ...patch } }
    }));

  return (
    <Field>
      <SectionHeader label="Table" expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <>
          <Description>
            Customize table header colors, alternating row colors, and header repetition.
          </Description>
          <div className="mt-2 space-y-2">
            <div className="flex items-end gap-4">
              <div>
                <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                  Header background
                  {autoBg && !explicitBg && (
                    <span className="ml-1 text-neutral-400 dark:text-neutral-500 italic">
                      (auto)
                    </span>
                  )}
                </Label>
                <ColorInput
                  value={explicitBg ?? autoBg ?? '#e2e8f0'}
                  textValue={explicitBg ?? ''}
                  onChange={(hex) => updateTable({ headerBackgroundColorHex: hex })}
                  placeholder={autoBg ?? '#e2e8f0'}
                  inputClassName="w-28"
                />
              </div>
              <div>
                <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                  Header text color
                  {autoText && !explicitText && (
                    <span className="ml-1 text-neutral-400 dark:text-neutral-500 italic">
                      (auto)
                    </span>
                  )}
                </Label>
                <ColorInput
                  value={explicitText ?? autoText ?? '#0f172a'}
                  textValue={explicitText ?? ''}
                  onChange={(hex) => updateTable({ headerColorHex: hex })}
                  placeholder={autoText ?? '#0f172a'}
                  inputClassName="w-28"
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                Alternating row color
              </Label>
              <ColorInput
                value={value?.elements?.table?.zebraStripeColorHex ?? '#f8fafc'}
                textValue={value?.elements?.table?.zebraStripeColorHex ?? ''}
                onChange={(hex) => updateTable({ zebraStripeColorHex: hex })}
                placeholder="#f8fafc"
                inputClassName="w-28"
              />
            </div>
            <CheckboxField>
              <Checkbox
                color="sky"
                checked={value?.elements?.table?.repeatHeaders ?? true}
                onChange={(checked) => updateTable({ repeatHeaders: checked ? undefined : false })}
              />
              <Label className="text-sm">Repeat headers on each page</Label>
            </CheckboxField>
          </div>
        </>
      )}
    </Field>
  );
}

function PageSection({
  value,
  onChange,
  hasHeaderFooterImageSlots,
  expanded,
  onToggle
}: SectionProps & { hasHeaderFooterImageSlots: boolean }) {
  const updateMargin = (
    patch: Partial<NonNullable<NonNullable<HtmlDocumentTemplateStyleInput['page']>['margin']>>
  ) =>
    onChange((s) => ({
      ...s,
      page: { ...s?.page, margin: { ...s?.page?.margin, ...patch } }
    }));

  const lockedMarginInput = (label: 'Top' | 'Bottom', valueIn: number | undefined | null) => (
    <div>
      <Label
        className={clsx(
          'mb-0.5 flex items-center gap-1 text-xs',
          hasHeaderFooterImageSlots
            ? 'text-neutral-400 dark:text-neutral-500'
            : 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        {label}
        {hasHeaderFooterImageSlots && <LockClosedIcon className="w-3 h-3" />}
      </Label>
      {hasHeaderFooterImageSlots ? (
        <Input
          type="text"
          readOnly
          value={valueIn ?? '—'}
          className="w-full bg-neutral-50 dark:bg-neutral-900 text-neutral-400 dark:text-neutral-500 cursor-not-allowed"
        />
      ) : (
        <Input
          type="number"
          inputMode="decimal"
          step="0.25"
          placeholder="0.75"
          value={valueIn ?? ''}
          onChange={(e) => {
            const n = e.target.value === '' ? undefined : parseFloat(e.target.value);
            updateMargin(label === 'Top' ? { topIn: n } : { bottomIn: n });
          }}
          className="w-full"
        />
      )}
    </div>
  );

  return (
    <Field>
      <SectionHeader label="Page" expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <>
          <Description>Customize page margins.</Description>
          <div className="mt-2 space-y-3">
            <div>
              <Label className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                Margins (in)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="mb-0.5 block text-xs text-neutral-500 dark:text-neutral-400">
                    Left
                  </Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.25"
                    placeholder="0.75"
                    value={value?.page?.margin?.leftIn ?? ''}
                    onChange={(e) => {
                      const n = e.target.value === '' ? undefined : parseFloat(e.target.value);
                      updateMargin({ leftIn: n });
                    }}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label className="mb-0.5 block text-xs text-neutral-500 dark:text-neutral-400">
                    Right
                  </Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.25"
                    placeholder="0.75"
                    value={value?.page?.margin?.rightIn ?? ''}
                    onChange={(e) => {
                      const n = e.target.value === '' ? undefined : parseFloat(e.target.value);
                      updateMargin({ rightIn: n });
                    }}
                    className="w-full"
                  />
                </div>
                {lockedMarginInput('Top', value?.page?.margin?.topIn)}
                {lockedMarginInput('Bottom', value?.page?.margin?.bottomIn)}
              </div>
              {hasHeaderFooterImageSlots && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                  Top and bottom margins are locked to fit the header/footer image. To change the
                  margin, adjust the gaps on the largest image in the header or footer.
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </Field>
  );
}
